import { BrowserRouter, Routes, Route, NavLink, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  LayoutDashboard,
  FolderOpen,
  Upload,
  FileText,
  CheckCircle,
  Clock,
  TrendingUp,
  Trash2,
  Loader2,
  Menu,
  X,
  LogIn,
  LogOut,
  CalendarDays,
  BarChart3,
  Shield,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./components/ui/table";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import "@/App.css";
import CrmTech from "./CrmTech";
import DocumentosAdicionales from "./components/DocumentosAdicionales";
import JustificanteSection from "./components/JustificanteSection";
import DossierDetailV2 from "./components/admin/DossierDetailV2";
import DossierDetailRes022 from "./components/admin/DossierDetailRes022";
import PlanningV2 from "./components/admin/PlanningV2";
import EditDossierV2 from "./components/admin/EditDossierV2";
import EditDossierRes022 from "./components/admin/EditDossierRes022";
import CorbeilleV2 from "./components/admin/CorbeilleV2";
import NewDossierV2 from "./components/admin/NewDossierV2";
import DossiersList from "./components/admin/DossiersList";
import DossiersListRes022 from "./components/admin/DossiersListRes022";
import Estadisticas from "./components/admin/Estadisticas";
import TechAdmin from "./components/admin/TechAdmin";
import MonelegAdmin from "./components/moneleg/MonelegAdmin";
import MonelegPortal from "./components/moneleg/MonelegPortal";
import CopiaSeguridad from "./components/admin/CopiaSeguridad";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const API_DOCS = `${BACKEND_URL}/api/docs-v2`;

// Fast native download - simple <a> tag click
// Backend bugs are fixed, streaming works, Content-Disposition: attachment keeps user in CRM
const triggerDownload = (url) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = '';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => document.body.removeChild(link), 200);
};

const triggerDownloadZip = triggerDownload;

// Configure axios to always send cookies
axios.defaults.withCredentials = true;

// ===================== AUTH COMPONENTS =====================

// Login Page
const LoginPage = () => {
  const [checking, setChecking] = useState(true);
  const [mode, setMode] = useState("login"); // login or register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (res.data?.user_id) {
          navigate("/", { replace: true });
          return;
        }
      } catch {}
      setChecking(false);
    };
    checkAuth();
  }, [navigate]);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === "register" ? `${API}/auth/register` : `${API}/auth/login`;
      const payload = mode === "register" 
        ? { email, password, name } 
        : { email, password };
      const res = await axios.post(endpoint, payload, { withCredentials: true });
      if (res.data?.pending) {
        toast.success(res.data.message || "Cuenta creada. Pendiente de aprobación.");
        setMode("login");
        return;
      }
      navigate("/", { replace: true, state: { user: res.data } });
    } catch (error) {
      const msg = error.response?.data?.detail || "Error de autenticación";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900" data-testid="login-page">
      <div className="w-full max-w-md mx-4">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-9 h-9 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl font-bold text-white font-['Plus_Jakarta_Sans']">CRM GRUPO AISLA-SOLAR</h1>
          <p className="text-slate-400 mt-2">CRM de gestión de expedientes CAE</p>
        </div>
        <Card className="border-slate-700 bg-slate-800/50 backdrop-blur">
          <CardContent className="pt-6 pb-6 px-6 space-y-5">
            {/* Email/Password Form */}
            <form onSubmit={handleEmailAuth} className="space-y-3">
              {mode === "register" && (
                <div>
                  <Label className="text-slate-300 text-sm">Nombre</Label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tu nombre completo"
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                    data-testid="register-name-input"
                    required
                  />
                </div>
              )}
              <div>
                <Label className="text-slate-300 text-sm">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="email-input"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Contraseña</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 caracteres"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="password-input"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-green-600 hover:bg-green-700 font-medium"
                data-testid="email-auth-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <LogIn className="w-4 h-4 mr-2" />
                )}
                {mode === "register" ? "Crear cuenta" : "Iniciar sesión"}
              </Button>
            </form>

            <button
              type="button"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); }}
              className="w-full text-sm text-slate-400 hover:text-slate-200 transition-colors"
              data-testid="toggle-auth-mode"
            >
              {mode === "login" 
                ? "¿No tienes cuenta? Crear una cuenta" 
                : "¿Ya tienes cuenta? Iniciar sesión"}
            </button>

          </CardContent>
        </Card>
        <p className="text-center text-slate-500 text-sm mt-6">
          Plan de Aislamiento 100% Financiado
        </p>
      </div>
    </div>
  );
};


// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const [authState, setAuthState] = useState(null); // null=checking, true=ok, false=no
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async (attempt = 1) => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true, timeout: 10000 });
        setUser(res.data);
        setAuthState(true);
      } catch (err) {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          setAuthState(false);
          navigate("/login", { replace: true });
        } else if (attempt < 3) {
          setTimeout(() => checkAuth(attempt + 1), 2000);
        } else {
          setAuthState(false);
          navigate("/login", { replace: true });
        }
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  if (authState === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (authState === false) return null;

  return children(user);
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    nuevo: { label: "Nuevo", className: "bg-blue-100 text-blue-800" },
    en_cours: { label: "En Curso", className: "bg-orange-100 text-orange-800" },
    enviado_greenflex: { label: "Enviado Greenflex", className: "bg-emerald-100 text-emerald-800" },
    enviado_moneleg: { label: "Enviado Moneleg", className: "bg-violet-100 text-violet-800" },
  };
  const config = statusConfig[status] || statusConfig.nuevo;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${config.className}`}>
      {config.label}
    </span>
  );
};

// Sidebar Component
const Sidebar = ({ isOpen, onClose, user, onLogout, collapsed, onToggleCollapse }) => {
  const userRole = user?.role || "admin";

  const allNavItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard", roles: ["admin", "secretaire"] },
    { path: "/dossiers", icon: FolderOpen, label: "Dossiers", roles: ["admin", "secretaire"] },
    { path: "/dossiers-res022", icon: FolderOpen, label: "Dossier RES022", roles: ["admin", "secretaire"] },
    { path: "/nuevo", icon: Upload, label: "Nuevo Dossier", roles: ["admin", "secretaire"] },
    { path: "/planning", icon: CalendarDays, label: "Planning", roles: ["admin", "secretaire", "commercial"] },
    { path: "/estadisticas", icon: BarChart3, label: "Estadísticas", roles: ["admin", "secretaire", "commercial"] },
    { path: "/corbeille", icon: Trash2, label: "Papelera", roles: ["admin", "secretaire", "commercial"] },
    { path: "/acces", icon: Shield, label: "Accès", roles: ["admin"] },
    { path: "/moneleg-admin", icon: FolderOpen, label: "Dossier Moneleg", roles: ["admin"] },
  ];
  
  const navItems = allNavItems.filter(item => item.roles.includes(userRole));

  const roleBadge = {
    admin: { label: "Admin", color: "bg-red-500/20 text-red-300" },
    secretaire: { label: "Secrétaire", color: "bg-blue-500/20 text-blue-300" },
    commercial: { label: "Commercial", color: "bg-amber-500/20 text-amber-300" },
  };

  const displayRole = user?.is_super_admin ? "super_admin" : userRole;
  const displayBadge = user?.is_super_admin 
    ? { label: "Super Admin", color: "bg-purple-500/20 text-purple-300" }
    : roleBadge[userRole];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}

      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        bg-slate-900 text-slate-300 h-screen flex flex-col shrink-0
        transition-all duration-200 ease-in-out
        ${collapsed ? 'w-16' : 'w-64'}
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Header */}
        <div className={`p-3 flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div>
                <h1 className="text-base font-bold text-white font-['Plus_Jakarta_Sans']">CRM AISLA-SOLAR</h1>
              </div>
            </div>
          )}
          {collapsed && (
            <span className="text-xs font-bold text-white">AS</span>
          )}
          <button
            className="lg:hidden text-slate-400 hover:text-white"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        {/* Toggle button desktop */}
        <div className={`hidden lg:flex px-2 mb-2 ${collapsed ? 'justify-center' : 'justify-end'}`}>
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded-md text-slate-500 hover:text-white hover:bg-slate-700 transition-colors"
            title={collapsed ? "Expandir menú" : "Colapsar menú"}
          >
            {collapsed
              ? <ChevronRight size={16} />
              : <ChevronLeft size={16} />
            }
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 space-y-0.5">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium transition-colors
                ${collapsed ? 'justify-center' : ''}
                ${isActive
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`
              }
              end={item.path === "/"}
            >
              <item.icon size={20} strokeWidth={1.5} className="shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Bottom user section */}
        <div className="p-2 space-y-2">
          {user && (
            <div className={`bg-slate-800 rounded-lg p-2 flex items-center gap-2 ${collapsed ? 'justify-center' : ''}`}>
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full shrink-0" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-bold shrink-0">
                  {user.name?.[0] || "U"}
                </div>
              )}
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-white truncate">{user.name}</p>
                  {displayBadge && (
                    <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full font-medium ${displayBadge.color}`}>
                      {displayBadge.label}
                    </span>
                  )}
                </div>
              )}
              {!collapsed && (
                <button onClick={onLogout} className="text-slate-400 hover:text-white p-1 shrink-0" data-testid="logout-btn" title="Cerrar sesión">
                  <LogOut size={15} />
                </button>
              )}
              {collapsed && (
                <button onClick={onLogout} className="hidden" data-testid="logout-btn" />
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

// Dashboard Page
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentDossiers, setRecentDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, dossiersRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/dossiers`)
        ]);
        setStats(statsRes.data);
        setRecentDossiers((dossiersRes.data.dossiers || dossiersRes.data).slice(0, 5));
      } catch (error) {
        toast.error("Error al cargar estadísticas");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const kpiCards = [
    { title: "Total Dossiers", value: stats?.total || 0, icon: FolderOpen, color: "text-slate-600", bg: "bg-slate-100", filter: "todos", path: "/dossiers-res022" },
    { title: "Nuevos", value: stats?.nouveau || 0, icon: Clock, color: "text-blue-600", bg: "bg-blue-100", filter: "nuevo", path: "/dossiers-res022" },
    { title: "En Curso", value: stats?.en_cours || 0, icon: TrendingUp, color: "text-orange-600", bg: "bg-orange-100", filter: "en_cours", path: "/dossiers-res022" },
    { title: "Enviado Moneleg", value: stats?.enviado_moneleg || 0, icon: CheckCircle, color: "text-violet-600", bg: "bg-violet-100", filter: "enviado_moneleg", path: "/dossiers-res022" },
    { title: "Enviado Greenflex", value: stats?.enviado_greenflex || 0, icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-100", filter: "enviado_greenflex", path: "/dossiers-res022" },
  ];

  return (
    <div className="space-y-6 animate-fade-in-up">
      <h1 className="text-xl font-bold text-slate-900">Dashboard</h1>

      {/* KPI Cards — compact */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpiCards.map((kpi, index) => (
          <div
            key={kpi.title}
            onClick={() => navigate(`${kpi.path || "/dossiers"}?filter=${kpi.filter}`)}
            data-testid={`kpi-card-${kpi.filter}`}
            className="bg-white border border-slate-200 rounded-xl px-4 py-3 cursor-pointer hover:border-slate-300 hover:shadow-sm transition-all flex items-center justify-between gap-3"
          >
            <div>
              <p className="text-xs text-slate-400 font-medium">{kpi.title}</p>
              <p className="text-2xl font-bold text-slate-900 mt-0.5">{kpi.value}</p>
            </div>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${kpi.bg}`}>
              <kpi.icon className={`w-4 h-4 ${kpi.color}`} strokeWidth={2} />
            </div>
          </div>
        ))}
      </div>

      {/* Chart — compact */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold text-slate-700">Expedientes por mes</p>
          <span className="text-xs text-slate-400">7 últimos meses</span>
        </div>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats?.monthly_stats || []} barSize={24}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={24} />
              <Tooltip
                cursor={{ fill: '#f8fafc' }}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                formatter={(v) => [v, 'Expedientes']}
              />
              <Bar dataKey="count" fill="#22c55e" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

    </div>
  );
};

const AppLayout = ({ children, user }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "true");
  const navigate = useNavigate();

  const handleToggleCollapse = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem("sidebar_collapsed", String(next));
      return next;
    });
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} user={user} onLogout={handleLogout} collapsed={collapsed} onToggleCollapse={handleToggleCollapse} />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile header */}
        <header className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-slate-100"
            >
              <Menu size={20} />
            </button>
            <span className="font-bold text-slate-900">CRM AISLA-SOLAR</span>
          </div>
          {user && (
            <button onClick={handleLogout} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
              <LogOut size={18} />
            </button>
          )}
        </header>

        <main className="flex-1 p-6 md:p-10 overflow-auto flex flex-col">
          <CopiaSeguridad modo="banner" />
          {children}
        </main>
      </div>
    </div>
  );
};

const AppRouter = () => {
  const location = useLocation();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/tech-v2" element={<CrmTech />} />
      <Route path="/moneleg" element={<MonelegPortal />} />
      <Route path="/*" element={
        <ProtectedRoute>
          {(user) => {
            const userRole = user?.role || "admin";
            return (
            <AppLayout user={user}>
              <Routes>
                <Route path="/" element={userRole === "commercial" ? <Navigate to="/planning" replace /> : <Dashboard />} />
                <Route path="/dossiers" element={userRole === "commercial" ? <Navigate to="/planning" replace /> : <DossiersList isAdmin={userRole === "admin"} />} />
                <Route path="/dossiers-res022" element={userRole === "commercial" ? <Navigate to="/planning" replace /> : <DossiersListRes022 isAdmin={userRole === "admin"} />} />
                <Route path="/nuevo" element={userRole === "commercial" ? <Navigate to="/planning" replace /> : <NewDossierV2 />} />
                <Route path="/planning" element={<PlanningV2 user={user} />} />
                <Route path="/estadisticas" element={<Estadisticas />} />
                <Route path="/corbeille" element={<CorbeilleV2 isAdmin={userRole === "admin"} />} />
                <Route path="/acces" element={userRole === "admin" ? <TechAdmin /> : <Navigate to="/" replace />} />
                <Route path="/tech-admin" element={<Navigate to="/acces" replace />} />
                <Route path="/moneleg-admin" element={userRole === "admin" ? <MonelegAdmin /> : <Navigate to="/" replace />} />
                <Route path="/dossier/:id" element={userRole === "commercial" ? <Navigate to="/planning" replace /> : <DossierDetailV2 />} />
                <Route path="/dossier-res022/:id" element={userRole === "commercial" ? <Navigate to="/planning" replace /> : <DossierDetailRes022 />} />
                <Route path="/dossier/:id/edit" element={userRole === "commercial" ? <Navigate to="/planning" replace /> : <EditDossierV2 />} />
                <Route path="/dossier-res022/:id/edit" element={userRole === "commercial" ? <Navigate to="/planning" replace /> : <EditDossierRes022 />} />
              </Routes>
            </AppLayout>
            );
          }}
        </ProtectedRoute>
      } />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
